import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import qs from "qs";
import { GreenroomState as GS } from "../../stores/types/types";
import { RoomStates } from "../../types/enums";
import { setCurrentGraphicsId } from "../../stores/actions/graphics";
import { setSelectedMedia } from "../../stores/actions/media";
import { setSelectedMediaTemplate } from "../../stores/actions/generate-content";
import { setActiveTab, setRightDrawerOpen, setActiveSubTab } from "../../stores/actions/tabs";
import { TABS } from "../../stores/types/tabs";
import { MEDIA_SETTINGS_TABS } from "../../stores/types/session";
import useShots from "../../contexts/shots/provider";
import type { MediaItem } from "../../stores/types/media";
import logger from '../../utils/logger-rum';

export default function useJumpToContent() {
	const dispatch = useDispatch();
	const didLoad = useRef(false);

	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);

	const { handleSelectShot } = useShots();

	useEffect(() => {
		if (didLoad.current) return;

		// Only apply deep-linking when IN showbuilder mode
		if (roomState !== RoomStates.showbuilder) {
			return;
		}

		// Wait for data to load before proceeding
		// Shots array is backfilled with a shot_id of 0, so check for that
		const shotsLoaded = shots.length > 0 && shots[0]?.shot_id !== 0;
		if (!shotsLoaded || graphics.length === 0 || media.length === 0) {
			return;
		}

		// Parse query string
		const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });

		// @ts-ignore
		const strShotId = params.shots?.[0];
		if (strShotId) {
			const shotId = Number(strShotId);
			const shot = shots.find((s) => s.shot_id === shotId);

			if (shot) {
				handleSelectShot(shotId);
			} else {
				logger.error(`[useJumpToContent] Shot with ID ${shotId} not found`);
				didLoad.current = true;
				return;
			}
		}

		// Handle graphic parameter
		if (params.graphic) {
			const graphicId = Number(params.graphic);
			const graphic = graphics.find((g) => g.id === graphicId);

			if (graphic) {
				const contentTemplate = graphic?.template_type
					? contentTemplates.find((x) => x.type === graphic.template_type)
					: undefined;

				dispatch(setCurrentGraphicsId(graphicId));
				dispatch(setSelectedMediaTemplate(null, ''));

				// Only open drawer if not a strict stylepack
				if (!contentTemplate?.data?.strict) {
					dispatch(setActiveTab(TABS.OVERLAYS));
					dispatch(setRightDrawerOpen(true));
				}
			} else {
				logger.error(`[useJumpToContent] Graphic with ID ${graphicId} not found`);
			}
		}

		// Handle media parameter
		if (params.media) {
			const mediaId = Number(params.media);
			const mediaItem = media.find((m: MediaItem) => m.id === mediaId);

			if (mediaItem) {
				const generateContentTemplate = mediaItem?.template_type
					? contentTemplates.find((x) => x.type === mediaItem.template_type)
					: undefined;

				dispatch(setSelectedMediaTemplate(mediaItem, generateContentTemplate?.type ?? ''));
				dispatch(setCurrentGraphicsId(0));

				// Only open drawer if not a strict stylepack
				if (!generateContentTemplate?.data?.strict) {
					dispatch(setRightDrawerOpen(true));
					dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
					dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, MEDIA_SETTINGS_TABS.CONTENT));
				}
			} else {
				logger.error(`[useJumpToContent] Media with ID ${mediaId} not found`);
			}
		}

		// Mark as loaded after successful processing
		didLoad.current = true;
	}, [roomState, shots, graphics, media, contentTemplates, handleSelectShot, dispatch]);

	return {};
}